// SPDX-License-Identifier: MIT
// ConcurrencyController - lightweight promise pool for downloads (Phase 4 scaffold)

import { ConcurrencyConfig } from './types';

export type TaskFn<T> = () => Promise<T>;

export class ConcurrencyController {
  private maxParallel: number;
  private running = 0;
  private queue: Array<() => void> = [];

  constructor(cfg: ConcurrencyConfig) {
    this.maxParallel = Math.max(1, cfg.maxParallel);
  }

  run<T>(fn: TaskFn<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const start = () => {
        this.running++;
        fn()
          .then(res => {
            this.running--;
            this.drain();
            resolve(res);
          })
          .catch(err => {
            this.running--;
            this.drain();
            reject(err);
          });
      };
      if (this.running < this.maxParallel) {
        start();
      } else {
        this.queue.push(start);
      }
    });
  }

  private drain() {
    while (this.running < this.maxParallel && this.queue.length) {
      const next = this.queue.shift();
      if (next) next();
    }
  }

  setLimit(n: number) {
    this.maxParallel = Math.max(1, n);
    this.drain();
  }

  getStatus() {
    return { running: this.running, queued: this.queue.length, maxParallel: this.maxParallel };
  }
}
